import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, ImagePlus, Users, ShieldCheck, LogOut, BarChart3, ExternalLink, Sprout, Layers, Images, FileText, HeartHandshake, TrendingUp, Radar, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "admin-nav-dashboard" },
    { to: "/admin/submissions", label: "Submissions", icon: Inbox, testid: "admin-nav-submissions" },
    { to: "/admin/volunteers", label: "Volunteer Applications", icon: HeartHandshake, testid: "admin-nav-volunteers" },
    { to: "/admin/analytics", label: "Donor Analytics", icon: TrendingUp, testid: "admin-nav-analytics" },
    { to: "/admin/intelligence", label: "Executive Intelligence", icon: Radar, testid: "admin-nav-intelligence" },
    { to: "/admin/analytics-settings", label: "Analytics Settings", icon: Settings2, testid: "admin-nav-analytics-settings" },
    { to: "/admin/impact-stats", label: "Impact Stats", icon: BarChart3, testid: "admin-nav-impact" },
    { to: "/admin/impact-metrics", label: "Impact Metrics", icon: BarChart3, testid: "admin-nav-impact-metrics" },
    { to: "/admin/programme-funding", label: "Programme Funding", icon: Sprout, testid: "admin-nav-funding" },
    { to: "/admin/projects", label: "Impact Hub · Projects", icon: Layers, testid: "admin-nav-projects" },
    { to: "/admin/media-inventory", label: "Media Inventory", icon: Images, testid: "admin-nav-media-inventory" },
    { to: "/admin/impact-snapshot", label: "Impact Snapshot PDF", icon: FileText, testid: "admin-nav-impact-snapshot" },
    { to: "/admin/brand-assets", label: "Brand Assets", icon: ImagePlus, testid: "admin-nav-brand" },
    { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
    { to: "/admin/security", label: "Security", icon: ShieldCheck, testid: "admin-nav-security" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    async function onLogout() {
        await logout();
        toast.success("Signed out");
        nav("/admin/login", { replace: true });
    }

    return (
        <div className="min-h-screen bg-[#f5f7f4]" data-testid="admin-layout">
            <aside className="fixed inset-y-0 left-0 w-64 bg-[color:var(--gsf-green-900)] text-white p-6 hidden md:flex flex-col">
                <Link to="/admin" className="flex items-center gap-3 mb-8" data-testid="admin-brand-link">
                    <Logo variant="icon" size={40} className="rounded-lg" />
                    <div>
                        <div className="font-serif text-lg leading-tight">GSF Admin</div>
                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--gsf-gold-500)]">Warren Park Connect</div>
                    </div>
                </Link>

                <nav className="flex-1 space-y-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.exact}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive ? "bg-white/10 text-[color:var(--gsf-gold-500)]" : "text-white/80 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <n.icon size={17} strokeWidth={1.8} />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-4 pt-4 border-t border-white/10 text-sm">
                    <div className="mb-3 text-white/70" data-testid="admin-user-info">
                        <div className="font-medium text-white truncate">{user?.email}</div>
                        <div className="text-xs uppercase tracking-widest text-[color:var(--gsf-gold-500)] mt-1">{user?.role?.replace("_", " ")}</div>
                    </div>
                    <Link to="/" target="_blank" className="flex items-center gap-2 text-white/80 hover:text-white text-xs mb-2" data-testid="admin-view-site">
                        <ExternalLink size={14} /> View public site
                    </Link>
                    <button onClick={onLogout} className="flex items-center gap-2 text-white/80 hover:text-white text-sm" data-testid="admin-logout">
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden bg-[color:var(--gsf-green-900)] text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Logo variant="icon" size={32} className="rounded-md" />
                    <span className="font-serif text-lg">GSF Admin</span>
                </div>
                <button onClick={onLogout} className="text-sm" data-testid="m-admin-logout">Sign out</button>
            </div>
            <div className="md:hidden bg-white border-b border-gray-200 p-3 flex gap-1 overflow-x-auto">
                {NAV.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.exact} data-testid={`m-${n.testid}`}
                        className={({ isActive }) => `text-xs px-3 py-2 rounded-full whitespace-nowrap ${isActive ? "bg-[color:var(--gsf-soft-green)] text-[color:var(--gsf-green-700)]" : "text-gray-500"}`}>
                        {n.label}
                    </NavLink>
                ))}
            </div>

            <main className="md:pl-64">
                <div className="p-6 sm:p-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
